import { useId, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import cn from '../../lib/cn';

/**
 * Accessible accordion. One panel open at a time; clicking the open item
 * closes it. Items are `{ id, question, answer }`.
 */
export default function Accordion({ items, defaultOpenId = null, className }) {
  const [openId, setOpenId] = useState(defaultOpenId);
  const baseId = useId();

  return (
    <div className={cn('divide-y divide-ink-100 overflow-hidden rounded-xl2 border border-ink-100 bg-white shadow-soft', className)}>
      {items.map((item) => {
        const isOpen = openId === item.id;
        const panelId = `${baseId}-panel-${item.id}`;
        const buttonId = `${baseId}-button-${item.id}`;

        return (
          <div key={item.id}>
            <h3>
              <button
                type="button"
                id={buttonId}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenId(isOpen ? null : item.id)}
                className={cn(
                  'flex w-full items-center justify-between gap-4 px-5 py-5 text-left transition-colors sm:px-6',
                  isOpen ? 'bg-brand-50/40' : 'hover:bg-ink-50'
                )}
              >
                <span
                  className={cn(
                    'font-display text-[0.98rem] font-semibold leading-snug sm:text-base',
                    isOpen ? 'text-brand-800' : 'text-ink-900'
                  )}
                >
                  {item.question}
                </span>
                <span
                  className={cn(
                    'grid h-8 w-8 flex-none place-items-center rounded-full border transition-all duration-300',
                    isOpen
                      ? 'rotate-45 border-brand-600 bg-brand-600 text-white'
                      : 'border-ink-200 text-ink-500'
                  )}
                >
                  <Plus className="h-4 w-4" strokeWidth={2} />
                </span>
              </button>
            </h3>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden bg-brand-50/40"
                >
                  <p className="px-5 pb-6 pr-14 text-sm leading-relaxed text-ink-600 sm:px-6 sm:pr-16">
                    {item.answer}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}
